import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { mediaStreamTracker } from '@/shared/utils/mediaStreamTracker';

import { organizationModel } from '@/entities/organization';
import { templatesModel } from '@/entities/templates';

import {
  audioRecordingService,
  getRecordingLanguageCandidates,
  getStoredOrganizationLanguage,
  pickRecordingLanguage,
  saveOrganizationLanguage,
  useMicrophonePermissions,
  useSoundCheck,
} from '@/features/audioRecording';
import { getAvailableTemplateLanguages } from '@/features/templateList';

// What a recording starts with, on the template choice and on the start page:
// the clinic's catalog, the microphone (asked for once, its level followed)
// and the language — the one chosen for the schema, else the schema's own,
// else the clinic's usual one.
export const useRecordingSetup = (schemaId?: string) => {
  const dispatch = useAppDispatch();
  const { locale } = useIntl();
  const [languageChoice, setLanguageChoice] = useState<{
    language: string;
    schemaId?: string;
  }>();
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const stopMonitoringRef = useRef<(() => void) | null>(null);

  const organizationId = useAppSelector(
    organizationModel.selectors.selectRelevantOrganizationID,
  );
  const organizationLanguage = useAppSelector(
    organizationModel.selectors.selectRelevantOrganizationLanguage,
  );
  const v2Enabled = useAppSelector(
    organizationModel.selectors.selectTranscriptorV2Enabled,
  );
  const schemas = useAppSelector(
    templatesModel.selectors.selectPublishedEncounterSchemas,
  );
  const templates = useAppSelector(
    templatesModel.selectors.selectActiveDocumentTemplates,
  );

  const microphone = useMicrophonePermissions();
  const { soundCheckStatus } = useSoundCheck(
    microphone.audioLevel,
    microphone.hasPermission === true && !!microphone.selectedDevice,
  );

  const stopMonitoring = () => {
    stopMonitoringRef.current?.();
    stopMonitoringRef.current = null;
  };

  useEffect(() => {
    if (!organizationId) {
      return;
    }
    let active = true;
    setCatalogLoaded(false);
    const request = {
      organization_id: organizationId,
      ...(v2Enabled && organizationLanguage
        ? { language: organizationLanguage }
        : {}),
    };
    void Promise.allSettled([
      dispatch(templatesModel.fetchEncounterSchemas(request)).unwrap(),
      dispatch(templatesModel.fetchDocumentTemplates(request)).unwrap(),
    ]).then(() => {
      if (active) {
        setCatalogLoaded(true);
      }
    });
    return () => {
      active = false;
    };
    // `dispatch` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, organizationLanguage, v2Enabled]);

  useEffect(() => {
    if (microphone.hasPermission === null) {
      void microphone.checkMicrophonePermission();
    }
    // Asks once when the setup opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { selectedDevice, hasPermission, startAudioLevelMonitoring } =
      microphone;
    if (hasPermission !== true || !selectedDevice) {
      return undefined;
    }
    const monitoring = { active: true };
    void startAudioLevelMonitoring(selectedDevice).then((cleanup) => {
      if (monitoring.active) {
        stopMonitoringRef.current = cleanup ?? null;
        return;
      }
      cleanup?.();
    });
    return () => {
      monitoring.active = false;
      stopMonitoring();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [microphone.hasPermission, microphone.selectedDevice]);

  useEffect(
    () => () => {
      stopMonitoring();
      // The permission check and the level meter hold streams; a started
      // recording keeps its own and goes on in the recording widget.
      if (!audioRecordingService.isRecording()) {
        mediaStreamTracker.stopAllStreams();
      }
    },
    [],
  );

  const languageOptions = getAvailableTemplateLanguages({
    schemas,
    templates: [],
  });
  const chosenLanguage =
    languageChoice?.schemaId === schemaId
      ? languageChoice?.language
      : undefined;
  const schemaLanguage = schemas.find(
    (schema) => schema.id === schemaId,
  )?.language;
  const language =
    chosenLanguage ??
    schemaLanguage ??
    pickRecordingLanguage({
      options: languageOptions,
      candidates: getRecordingLanguageCandidates({
        stored: organizationId
          ? getStoredOrganizationLanguage(organizationId)
          : undefined,
        organization: organizationLanguage,
        templateLanguages: templates.map((template) => template.language),
        locale,
      }),
    });

  const setLanguage = (value: string) => {
    setLanguageChoice({ language: value, schemaId });
    if (organizationId) {
      saveOrganizationLanguage(organizationId, value);
    }
  };

  return {
    catalogLoaded,
    language,
    languageOptions,
    microphone: { ...microphone, stopMonitoring },
    soundCheckStatus,
    setLanguage,
  };
};
